/**
 * Render-backend preference — a per-device setting (Settings → Rendering).
 * "auto" (default): the engine uses the GPU for the warp stage + the multi-core CPU for
 * compositing whenever a GPU adapter (discrete OR integrated — Apple Silicon, Intel/AMD
 * iGPUs) is available; with no adapter it is pure multi-core CPU. "cpu": forces every stage
 * onto the CPU — the field escape hatch for a misbehaving GPU/driver, applied at RUNTIME
 * (no relaunch) via `engine.setRenderBackend`.
 * Persisted to localStorage so the choice survives reloads on this device; re-applied to
 * the engine at boot by `applyRenderBackend` (the native pref is process-scoped).
 */
import type { EngineBinding } from "@genz/engine-bind";

const KEY = "genz.renderBackend";
export type RenderBackend = "auto" | "cpu";

export function getRenderBackend(): RenderBackend {
  try {
    return localStorage.getItem(KEY) === "cpu" ? "cpu" : "auto";
  } catch {
    return "auto";
  }
}

/** Push the preference into the native engine (no-op binding on web/WASM — always CPU there). */
export function applyRenderBackend(engine: Pick<EngineBinding, "setRenderBackend">, b: RenderBackend): void {
  void engine.setRenderBackend?.(b).catch(() => { /* older desktop build without the command */ });
}

/** Persist + apply. Called from the Settings toggle. */
export function setRenderBackend(engine: Pick<EngineBinding, "setRenderBackend">, b: RenderBackend): void {
  try {
    localStorage.setItem(KEY, b);
  } catch {
    /* ignore (private mode / no storage) */
  }
  applyRenderBackend(engine, b);
}
